import React, { FC, ReactElement, useState } from 'react';
import { StatusReason, SupplierValidationRecordStatus } from '@mortee/domain/validationSystem';
import ClickEventPropagationBlocker from '@app/components/ClickEventPropagationBlocker';
import NakedDropdown from '@app/components/inputs/NakedDropdown';
import DropdownItem from '@app/components/inputs/DropdownItem';
import BigModal from '@app/components/BigModal';
import { BodySmallStartTransparentBlack600, Bold18TransparentBlack900 } from '@app/components/Text';
import styled from '@emotion/styled';
import Button from '@app/components/Button';
import TransitionLoader from '@app/components/TransitionLoader';
import { Checkbox, FormControlLabel } from '@mui/material';
import { arrayWithoutValue } from '@app/utils/arrayUtils';
import { observer } from 'mobx-react';
import useAppStores from '@app/hooks/useAppStores';
import MorteeMode from '@mortee/morteeMode';

const OTHER_REASON_TEXT = 'Other';
const OTHER_REASON_TEXT_MAX_LENGTH = 200;

export function compareReasonsWithOtherLast(a: StatusReason, b: StatusReason): number {
  const aIsOther = a.reason === OTHER_REASON_TEXT;
  const bIsOther = b.reason === OTHER_REASON_TEXT;
  if (aIsOther !== bIsOther) {
    return aIsOther ? 1 : -1;
  }
  return a.reason.localeCompare(b.reason);
}

interface Props {
  status: SupplierValidationRecordStatus;
  allowMultiple: boolean;
  initialValues: StatusReason[];
  title: string;
  explanation: string;
  className?: string;
  onSelect(reasons: StatusReason[], otherReasonText: string | null): void;
  onNotSelect(): void;
}

const StatusReasonSelector: FC<Props> = observer((props) => {
  const { validationSystemStore } = useAppStores<MorteeMode>();

  const { status, allowMultiple, initialValues, title, explanation, className, onSelect, onNotSelect } = props;

  const reasons =
    status === SupplierValidationRecordStatus.clarificationExternal
      ? validationSystemStore.clarificationExternalReasons
      : status === SupplierValidationRecordStatus.topUpRequired
        ? validationSystemStore.topUpRequiredReasons
        : validationSystemStore.cancelReasons;

  const [selectedReasonIds, setSelectedReasonIds] = useState<string[]>(initialValues.map((reason) => reason.id));
  const [otherReasonText, setOtherReasonText] = useState<string>('');

  const otherReason = (reasons.result ?? []).find((reason) => reason.reason === OTHER_REASON_TEXT);
  const isOtherSelected = otherReason !== undefined && selectedReasonIds.includes(otherReason.id);

  const displayedReasons = [...(reasons.result ?? [])].sort(compareReasonsWithOtherLast);

  const initialReasonIds = new Set(initialValues.map((reason) => reason.id));
  const wasOtherAlreadySelected = initialValues.some((reason) => reason.reason === OTHER_REASON_TEXT);
  const haveReasonsChanged =
    selectedReasonIds.length !== initialReasonIds.size || selectedReasonIds.some((id) => !initialReasonIds.has(id));
  const trimmedOtherText = otherReasonText.trim();
  // An explanation is mandatory when 'Other' is newly picked. It stays optional afterwards - the previous explanation
  // already lives in the record's internal comments, so a blank box just means "no new explanation to add".
  const isExplanationMissing = isOtherSelected && !wasOtherAlreadySelected && trimmedOtherText.length === 0;
  // Free text alone is a real change: it is appended to the comment thread. Neither changing means there is nothing to save.
  const hasNothingToSave = !haveReasonsChanged && trimmedOtherText.length === 0;

  function handleSubmit(): void {
    const selectedReasons = reasons.result?.filter((reason) => selectedReasonIds.includes(reason.id)) ?? [];
    onSelect(selectedReasons, isOtherSelected && trimmedOtherText.length > 0 ? trimmedOtherText : null);
  }

  function handleToggleReason(reasonId: string, checked: boolean): void {
    setSelectedReasonIds((current) => (checked ? [...current, reasonId] : arrayWithoutValue(current, reasonId)));
  }

  return (
    <ClickEventPropagationBlocker className={className}>
      <SelectReasonModal
        closeFunc={onNotSelect}
        title={<Bold18TransparentBlack900.div>{title}</Bold18TransparentBlack900.div>}
        dataTestId='status-reason-modal'
      >
        <SelectExplanationText>{explanation}</SelectExplanationText>
        <TransitionLoader loading={reasons.isInProgress()}>
          {allowMultiple ? (
            <ReasonCheckboxList
              aria-label={title}
              data-testid='select-status-reasons'
              onClick={(event): void => event.stopPropagation()}
            >
              {displayedReasons.map((reason) => (
                <ReasonCheckboxLabel
                  key={reason.id}
                  data-testid={`select-status-reason-${reason.id}`}
                  control={
                    <Checkbox
                      color='primary'
                      checked={selectedReasonIds.includes(reason.id)}
                      onChange={(event): void => handleToggleReason(reason.id, event.currentTarget.checked)}
                    />
                  }
                  label={reason.reason}
                />
              ))}
            </ReasonCheckboxList>
          ) : (
            <NakedDropdown
              name='drp-select-status-reason'
              dataTestId='drp-select-status-reason'
              accessibilityLabel={title}
              placeholder='Reason'
              isSearchable
              disablePortal
              value={selectedReasonIds[0]}
              onChange={(newValue): void => setSelectedReasonIds(newValue ? [newValue] : [])}
            >
              {
                displayedReasons.map((option) => (
                  <DropdownItem key={option.id} value={option.id} textWhenSelected={option.reason}>
                    {option.reason}
                  </DropdownItem>
                )) as ReactElement[]
              }
            </NakedDropdown>
          )}
          {isOtherSelected && (
            <OtherReasonTextArea
              data-testid='other-reason-text'
              aria-label='Other reason'
              placeholder={wasOtherAlreadySelected ? 'Add a new explanation (optional)' : 'Please specify'}
              maxLength={OTHER_REASON_TEXT_MAX_LENGTH}
              value={otherReasonText}
              onChange={(event): void => setOtherReasonText(event.currentTarget.value)}
            />
          )}
          <FormActions>
            <Button
              id='btn-status-reason-update-status'
              onClick={handleSubmit}
              colorScheme='primary'
              disabled={!reasons.isResolved() || selectedReasonIds.length === 0 || isExplanationMissing || hasNothingToSave}
            >
              UPDATE STATUS
            </Button>
          </FormActions>
        </TransitionLoader>
      </SelectReasonModal>
    </ClickEventPropagationBlocker>
  );
});

export default StatusReasonSelector;

const SelectReasonModal = styled(BigModal)`
  padding: 16px;
  width: 500px;
  box-shadow: var(--box-shadow-3);

  --block-padding: 0;
  --side-padding: 0;
`;

const SelectExplanationText = styled(BodySmallStartTransparentBlack600.div)`
  margin-bottom: 8px;
`;

const FormActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  margin-top: 16px;
`;

const ReasonCheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 280px;
  overflow-y: auto;
`;

// Align the checkbox to the first line when a reason wraps.
const ReasonCheckboxLabel = styled(FormControlLabel)`
  align-items: flex-start;
  margin: 0;

  .MuiFormControlLabel-label {
    padding-top: 9px;
    font-size: 14px;
    line-height: 1.4;
  }
`;

const OtherReasonTextArea = styled.textarea`
  margin-top: 8px;
  width: 100%;
  min-height: 60px;
  padding: 8px;
  box-sizing: border-box;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
`;
